import axios, { AxiosResponse } from 'axios';
import {
  ClientWarehouse,
  CountryThreshold,
  ClientWarehouseBE,
  QuestionType,
  Answer,
  FCTransfer,
  CrossedOss,
  UploadedFile,
  UserCompany,
  Country,
  ClientFeedback,
  ComplianceDocumentErrorsType,
  Channel,
  ClientDatevDetails,
  CompanyUser,
  UploadedCorrectionFile,
} from '../types';
import { User } from '../store/user/userSlice';
import {
  normalizeClientWarehousesFE,
  normalizeCountryThresholdFE,
} from '../normalization';
import { PurchasePricesUploadedFile } from '../../pages/PurchasePrices/PurchasePricesUploader';
import { Event } from '../../hooks';
import { client } from './axiosClient';
import { PricesFilterEnum, RolesEnum } from '../enums';
import logger from '../logger';
export * from './datev.service';
export * from './registration.service';
export * from './reports.service';
export * from './data-units.service';
export * from './amazon.service';
export * from './jtl.service';
export * from './billbee.service';
export * from './easybill.service';
export * from './nps.service';
export * from './coa.service';
export * from './invoice-documents.service';
export * from './proforma-invoices.service';
export * from './payments.service';
export * from './typeform.service';
export * from './changelog.service';
export * from './vat-ids.service';
export * from './invoice-book.service';
export * from './feedback.service';
export * from './files.service';
export * from './billing.service';
export * from './taxSettings.service';

interface LoginInput {
  username: string;
  password: string;
}

interface RespondToChallengeInput {
  username: string;
  code: string;
  session: string;
  challenge_name: string;
}

export interface LoginResponse {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  challenge_name?: string;
  session?: string;
  challenge_parameters?: Record<string, string>;
}

export const login = async (email: string, password: string) => {
  const res = await axios.post<LoginInput, AxiosResponse<LoginResponse>>(
    `${import.meta.env.VITE_APP_API_URL}/login`,
    {
      username: email,
      password,
    }
  );

  return res.data;
};

export const respondToAuthChallenge = async (
  input: RespondToChallengeInput
) => {
  const res = await axios.post<
    RespondToChallengeInput,
    AxiosResponse<LoginResponse>
  >(`${import.meta.env.VITE_APP_API_URL}/respond-to-auth-challenge`, input);

  return res.data;
};

export const getUser = async () => {
  const res = await client.get<User>('/get-user-data');
  return res.data;
};

export const postVisit = async () => {
  try {
    await client.post('/visit');
  } catch (err) {
    logger.error('Failed to record visit', err);
  }
};

export async function postEvent(id: string, event: Event) {
  return client.post(`/visitors/${id}/events`, event);
}

export const getClientThresholdByCountries = async (
  company_id?: string
): Promise<CountryThreshold[]> => {
  const res = await client.get<CountryThreshold[]>(
    '/client/threshold/countries',
    {
      params: {
        company_id,
      },
    }
  );

  return (res.data || [])
    .sort((a, b) => b.threshold.value - a.threshold.value)
    .map(normalizeCountryThresholdFE);
};

export const getClientWarehouses = async (
  company_id?: string
): Promise<ClientWarehouse[]> => {
  const res = await client.get<ClientWarehouseBE[]>('/client/warehouses', {
    params: {
      company_id,
    },
  });
  return (res.data || [])
    .sort((a, b) => b.count_of_sold_items - a.count_of_sold_items)
    .map(normalizeClientWarehousesFE);
};

export const patchUser = async (data: Partial<User>) => {
  const res = await client.patch('/user', data);
  return res.data;
};

export const patchAnswer = async (
  key: QuestionType,
  answer: boolean | string
) => {
  const res = await client.patch<Answer>(`/answer/${key}`, {
    answer,
  });
  return res.data;
};

export const getAnswer = async (key: QuestionType) => {
  const res = await client.get<boolean | string>(`/answer/${key}`);
  return res.data;
};

export const postAnswerWithComment = async (feedback: ClientFeedback) => {
  const res = await client.post<ClientFeedback>(
    '/answer/rate-with-comment',
    feedback
  );
  return res.data;
};

export const postUserTestingOpt = async () => {
  const res = await client.post('/user/testing-opt-in');
  return res.data;
};

export const postIntroBannerShown = async () => {
  const res = await client.post('/user/intro-banner-shown');
  return res.data;
};

export const postPostUpdateBannerShown = async () => {
  const res = await client.post('/user/post-update-banner-shown');
  return res.data;
};

export const getClientFCTransfers = async (company_id?: string) => {
  const res = await client.get<FCTransfer[]>('/client/amazon-fc-transfers', {
    params: {
      company_id,
    },
  });
  return res.data;
};
export const getCrossThreshold = async (company_id?: string) => {
  const res = await client.get<CrossedOss>(
    '/client/threshold/oss-crossed-quarter',
    {
      params: {
        company_id,
      },
    }
  );
  return res.data;
};
export const getUploadedDocumentsFiles = async (
  company_id: string,
  controller?: AbortController
) => {
  const res = await client.get<UploadedFile[]>('/client/uploaded-files', {
    signal: controller?.signal,
    params: {
      company_id,
    },
  });
  return res.data;
};
export const getUploadedCorrectionFiles = async (
  company_id: string,
  controller?: AbortController
) => {
  const res = await client.get<UploadedCorrectionFile[]>(
    '/client/correction-files',
    {
      signal: controller?.signal,
      params: {
        company_id,
      },
    }
  );
  return res.data;
};

export const getUploadedDocumentFileUrl = async (
  id: number,
  company_id?: string
) => {
  const res = await client.post<string>(
    `/client/uploaded-files/${id}/download`,
    undefined,
    {
      params: {
        company_id,
      },
    }
  );

  return res.data;
};

export const getUserCompanies = async () => {
  const res = await client.get<UserCompany[]>('/user/get-companies');
  return res.data;
};

export const getPurchasePricesCount = async (
  company_id: string,
  controller?: AbortController,
  lastMonthOnly?: boolean,
  year?: string,
  month?: string,
  filter?: PricesFilterEnum
) => {
  const res = await client.get<number>('/client/purchase-prices/count', {
    signal: controller?.signal,
    params: {
      company_id,
      lastMonthOnly: lastMonthOnly ? Number(lastMonthOnly) : undefined,
      year: year && month ? year : undefined,
      month: year && month ? month : undefined,
      pricesFilter: filter ?? PricesFilterEnum.MISSING,
    },
  });
  return res.data;
};

export const getPurchasePricesUploadedFiles = async (
  company_id: string,
  controller?: AbortController
) => {
  const res = await client.get<PurchasePricesUploadedFile[]>(
    '/client/purchase-prices/files',
    {
      signal: controller?.signal,
      params: {
        company_id,
      },
    }
  );
  return res.data;
};

export const postPurchasePricesTemplate = async (
  company_id: string | undefined,
  lastMonthOnly?: boolean,
  filter?: PricesFilterEnum,
  year?: string,
  month?: string
) => {
  const res = await client.post(
    '/client/purchase-prices/generate-template',
    undefined,
    {
      params: {
        company_id,
        lastMonthOnly: lastMonthOnly ? Number(lastMonthOnly) : undefined,
        pricesFilter: filter ?? PricesFilterEnum.MISSING,
        year: year && month ? year : undefined,
        month: year && month ? month : undefined,
      },
    }
  );
  return res.data;
};

export const getProductsMissingDataCount = async (
  company_id: string,
  allPeriods?: boolean
) => {
  const res = await client.get('/client/products/missing-data/count', {
    params: {
      company_id,
      allPeriods: allPeriods ? 1 : undefined,
    },
  });
  return res.data as number;
};

export const postProductsMissingDataDownload = async (
  company_id: string,
  allPeriods?: boolean
) => {
  const res = await client.post(
    '/client/products/missing-data/download',
    undefined,
    {
      params: {
        company_id,
        allPeriods: allPeriods ? 1 : undefined,
      },
    }
  );
  return res.data;
};

export interface ProductsIncompleteCount {
  currentPeriod: number;
  allPeriods: number;
}

export const getProductsIncompleteCount = async (
  company_id: string
): Promise<ProductsIncompleteCount> => {
  const res = await client.get('/client/products/incomplete/count', {
    params: {
      company_id,
    },
  });
  return res.data as ProductsIncompleteCount;
};

export const postProductsIncompleteDownload = async (
  company_id: string,
  allPeriods?: boolean
): Promise<string> => {
  const res = await client.post(
    '/client/products/incomplete/download',
    undefined,
    {
      params: {
        company_id,
        allPeriods: allPeriods ? 1 : undefined,
      },
    }
  );
  return res.data;
};

export const postNoManualDataNeeded = async (
  company_id: string,
  period: string
) => {
  const res = await client.post(`/client/no-manual-data-needed`, undefined, {
    params: {
      company_id,
      period,
    },
  });
  return res.data;
};

export const getCountries = async () => {
  const res = await client.get<Country[]>('/countries/search');
  return res.data;
};

export const postDownloadDataUnitsErrors = async (
  from: string,
  to: string,
  filter: string,
  lang: string,
  company_id: string | undefined,
  type?: 'incoming_invoices'
) => {
  const res = await client.post<string>(
    '/client/correction/download-errors',
    undefined,
    {
      params: {
        from,
        to,
        filter,
        lang,
        company_id,
        ...(type && { type }),
      },
    }
  );
  return res.data;
};

export const postDownloadCorrectionErrorsAsync = async (
  company_id: string | undefined,
  params: {
    from: string;
    to: string;
    filter: string;
    lang: string;
    type?: 'incoming_invoices';
  }
): Promise<{ jobId: number; status: string }> => {
  const res = await client.post(
    '/client/correction/download-errors/async',
    undefined,
    { params: { ...params, company_id } }
  );
  return res.data;
};

export const postDiscardFile = async (id: number, company_id?: string) => {
  const res = await client.post<string>(
    `/client/uploaded-files/${id}/discard`,
    undefined,
    {
      params: {
        company_id,
      },
    }
  );

  return res.data;
};

export const getComplianceDocumentErrors = async () => {
  const res = await client.get<ComplianceDocumentErrorsType[]>(
    '/compliance/document-errors'
  );
  return res.data;
};

export const getChannels = async () => {
  const res = await client.get<Channel[]>(`/channel`);
  return res.data;
};

export const postClientDatevDetails = async (
  company_id: string,
  body: Partial<ClientDatevDetails>
) => {
  const res = await client.post<
    Partial<ClientDatevDetails>,
    AxiosResponse<ClientDatevDetails>
  >(`/client/datev-details`, body, {
    params: {
      company_id,
    },
  });
  return res.data;
};

export interface PostClientDatevDetailsCOAAcceptedInput {
  chart_of_account_accepted_at: string;
  external_user_uuid: string;
}

export const postClientDatevDetailsCOAAccepted = async (
  company_id: string,
  body: PostClientDatevDetailsCOAAcceptedInput
) => {
  const res = await client.post<
    PostClientDatevDetailsCOAAcceptedInput,
    AxiosResponse<ClientDatevDetails>
  >(`/client/datev-details/chart-of-account-accepted-at`, body, {
    params: {
      company_id,
    },
  });
  return res.data;
};

export interface PostCOAConsultantNumberInput {
  consultant_number: number | null;
}

export const postCOAConsultantNumber = async (
  company_id: string,
  val: number | null
) => {
  const res = await client.post(
    `/client/datev-details/consultant-number`,
    {
      consultant_number: val,
    },
    {
      params: {
        company_id,
      },
    }
  );
  return res.data;
};

export interface PostCOAClientNumberInput {
  client_number: number | null;
}

export const postCOAClientNumber = async (
  company_id: string,
  val: number | null
) => {
  const res = await client.post<
    Partial<PostCOAClientNumberInput>,
    AxiosResponse<ClientDatevDetails>
  >(
    `/client/datev-details/client-number`,
    {
      client_number: val,
    },
    {
      params: {
        company_id,
      },
    }
  );
  return res.data;
};

export const getCompanyUsers = async (
  company_id: string,
  role: RolesEnum,
  controller?: AbortController
) => {
  const res = await client.get<CompanyUser[]>('/client/users', {
    signal: controller?.signal,
    params: {
      company_id,
      role,
    },
  });
  return res.data;
};
